import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useImagesLoaded } from '../../hooks/useImagesLoaded';
import { COLOR_BLACK, COLOR_WHITE, COLOR_STEAK_SAUCE, COLOR_GRAY_COSTA_MESA } from '../colors';
import { addCommas, getMonthName, numAbbreviate, splitName } from '../../utils';
import queryString from 'query-string';
import Heading from './Heading';
import TextBubble from './TextBubble';
import {
    IconDollar,
    IconActiveListing,
    IconNewListing,
    IconCalendar,
} from './Icons';

const ROOT_WIDTH = 900;
const ROOT_HEIGHT = 600;
const PIXEL_DENSITY_FACTOR = 2;
const KEYFRAME_COUNT = 10;
const getScaleFactor = (props)=> props && !isNaN(props.scaleFactor) ? props.scaleFactor : PIXEL_DENSITY_FACTOR;
const BG_PATH = './images/RecentlySoldAreaStatsShareable/bg.svg';

const useStyles = makeStyles((theme)=> ({
    root: {
        width: (props)=> ROOT_WIDTH * getScaleFactor(props),
        height: (props)=> ROOT_HEIGHT * getScaleFactor(props),
        position: 'relative',
        fontFamily: ['"LL Circular Pro"', 'Arial', 'Helvetica', 'sans-serif'],
        overflow: 'hidden',
        backgroundColor: COLOR_WHITE,
        textRendering: 'optimizeLegibility',
        color: COLOR_BLACK,
        borderRadius: (props)=> props && props.borderRadius ? props.borderRadius : 0,
    },
    viewport: {
        display: 'block',
        position: 'relative',
        width: '100%',
        height: '100%',
        zoom: (props)=> getScaleFactor(props),
    },
    flexContainer: {
        display: 'flex',
        minHeight: '100%',
    },
    flexChild: {
        flex: 1,
        boxSizing: 'border-box',
        padding: 24,
        minHeight: '100%',
        textAlign: 'center',
        fontSize: '24px',
        lineHeight: '32px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
    },
    cityName: {
        position: 'absolute',
        fontSize: 22,
        fontWeight: 500,
        lineHeight: '32px',
        left: 24,
        bottom: 24,
    },
    relativeDiv: {
        position: 'relative',
        width: '100%',
        height: '100%',
    },
    ctaText: {
        fontSize: '26px',
        lineHeight: '28px',
        fontWeight: 800,
    },
    disclaimerWrap: {
        position: 'absolute',
        width: '50%',
        bottom: 16,
        right: 24,
        color: COLOR_STEAK_SAUCE,
        fontSize: 10,
        lineHeight: '12px',
        textAlign: 'right',
    },
    disclaimerLogo: {
        textAlign: 'right',
    },
    disclaimer: {
        marginTop: 4,
        display: 'inline-block',
    },
    bodyWrap: {
        display: 'flex',
        alignItems: 'center start',
        flexDirection: 'row',
        padding: 24,
        fontSize: '24px',
        lineHeight: '32px',
    },
    listingInfoWrap: {
        minWidth: 336,
        marginTop: 8,
    },
    price: {
        fontWeight: 800,
        fontSize: '36px',
        lineHeight: '48px',
    },
    agentInfoWrap: {
        textAlign: 'left',
        marginTop: 24,
    },
    statsRow: {
        minHeight: 64,
        display: 'flex',
        alignItems: 'center',
        marginBottom: 20,
        textAlign: 'left',
    },
    statsIcon: {
        height: 48,
        width: 48,
        marginRight: 24,
    },
    statsLabel: {
        minWidth: 150,
        maxWidth: 150,
        marginRight: 16,
    },
    statsValue: {
        fontSize: 40,
        lineHeight: '48px',
        fontWeight: 800,
    },
}));

export default function RecentlySoldShareable(props) {
    const classes = useStyles(props);
    const location = useLocation();
    let parsedData = props.data || null;
    let currentFrame = props.frame || 1;
    const onImagesLoaded = props.onImagesLoaded;
    if(!parsedData) {
        try {
            const { data, frame } = queryString.parse(location.search);
            parsedData = JSON.parse(data);
            currentFrame = parseInt(frame, 10);
            if(currentFrame > KEYFRAME_COUNT) {
                currentFrame = currentFrame % KEYFRAME_COUNT;
            }
        } catch(e) {
            console.error(e);
        }
    }
    const {
        city,
        state,
        zip,
        address_line1,
        price,
        bedrooms,
        bathrooms,
        sqft,
        agent,
        stats,
    } = parsedData || {};
    const loadedBgs = useImagesLoaded([
        './images/lr-logo-disclaimer-grey.svg',
        BG_PATH,
    ]);
    const month = getMonthName();
    const [firstName, lastName] = splitName(agent && agent.name);
    const agentName = (firstName && lastName && firstName.toLowerCase() === lastName.toLowerCase())
        ? '' // fixing cases of bad BK data with buyer_agent_name = 'Hernandez Hernandez' etc
        : agent && agent.name;

    useEffect(() => {
        if(loadedBgs && !window.isLoaded) {
            if(typeof onImagesLoaded === 'function') {
                onImagesLoaded();
            } else {
                window.isLoaded = true;
            }
        }
    }, [loadedBgs, onImagesLoaded]);

    if(!parsedData) return null;

    return (
        <>
            <link rel="stylesheet" href="./css/ll-circular-pro.css" />
            <div className={classes.root} id="templateRoot">
                <div className={classes.viewport}>
                    <div className={classes.flexContainer}>

                        {/* Left Wrap */}
                        <div className={classes.flexChild} style={{
                            backgroundColor: COLOR_GRAY_COSTA_MESA,
                            backgroundImage: `url('${BG_PATH}')`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: '290px 0',
                        }}>
                            <div style={{marginLeft: 'auto', marginRight: 'auto'}}>
                                <Heading/>
                            </div>

                            <div style={{marginTop: 8, fontWeight: 600}}>
                                {address_line1}<br/>
                                {city}, {state} {zip}
                            </div>

                            <div className={classes.listingInfoWrap}>
                                <div className={classes.listingInfo}>
                                    {bedrooms > 0 && (<span>{bedrooms} bd</span>)}{' '}
                                    {bedrooms > 0 && (bathrooms > 0 || sqft > 0) && (<span>&bull;</span>)}{' '}

                                    {bathrooms > 0 && (<span>{bathrooms} ba</span>)}{' '}
                                    {bathrooms > 0 && (sqft > 0 && (<span>&bull;</span>))}{' '}

                                    {sqft > 0 && (<span>{addCommas(sqft)} sqft</span>)}{' '}
                                </div>

                                {/* Commented out temporarily due to compliance concerns for certain states - amish@listreports.com 2023-03-10
                                    {price && (<div className={classes.price}>${addCommas(price)}</div>)}
                                */}
                            </div>

                            <div style={{marginTop: 32, textAlign: 'left'}}>
                                <TextBubble />
                            </div>

                            <div className={classes.agentInfoWrap}>
                                {agent && (
                                    <div className={classes.agentInfo}>
                                        {agentName && (<div style={{fontWeight: 600}}>{agentName}</div>)}
                                        {agent.email && (<div>{agent.email}</div>)}
                                    </div>
                                )}
                            </div>
                        </div>


                        {/* Right Wrap */}
                        <div className={classes.flexChild}>

                            {/* Stats Wrap */}
                            <div style={{marginLeft: 8}}>
                                <div>Local Market Report for {month}</div>
                                {city && city.length && (<div style={{fontWeight: 600}}>{city}, {state}</div>)}

                                <div style={{marginTop: 32}}>
                                    <div className={classes.statsRow}>
                                        <div className={classes.statsIcon}><IconDollar/></div>
                                        <div className={classes.statsLabel}>Median<br/>price</div>
                                        <div className={classes.statsValue}>
                                            {(stats && stats.median_price) ? `$${numAbbreviate(stats.median_price, 0)}`: '-'}
                                        </div>
                                    </div>


                                    <div className={classes.statsRow}>
                                        <div className={classes.statsIcon}><IconActiveListing/></div>
                                        <div className={classes.statsLabel}>Active<br/>listings</div>
                                        <div className={classes.statsValue}>
                                            {(stats && stats.counts && stats.counts.hasOwnProperty('active_listings')) ? stats.counts.active_listings: '-'}
                                        </div>
                                    </div>


                                    <div className={classes.statsRow}>
                                        <div className={classes.statsIcon}><IconNewListing/></div>
                                        <div className={classes.statsLabel}>New listings<br/>(last 5 days)</div>
                                        <div className={classes.statsValue}>
                                            {(stats && stats.counts && stats.counts.hasOwnProperty('new')) ? stats.counts.new: '-'}
                                        </div>
                                    </div>


                                    <div className={classes.statsRow}>
                                        <div className={classes.statsIcon}><IconCalendar/></div>
                                        <div className={classes.statsLabel}>Median days<br/>on market</div>
                                        <div className={classes.statsValue}>
                                            {(stats && stats.hasOwnProperty('median_dom')) ? stats.median_dom: '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Disclaimer */}
                            <div className={classes.disclaimerWrap}>
                                <img className={classes.disclaimerLogo} src="./images/lr-logo-disclaimer-grey.svg" height={11} alt="LR Logo" />
                                <div className={classes.disclaimer}>
                                    The information in this report is from third-party sources
                                    and its accuracy cannot be guaranteed
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
